import { apiClient } from "./client";
import type {
  OutputField,
  OutputFieldCreate,
  OutputFieldUpdate,
  OutputFieldsPaginated,
  OutputFieldGroup,
  OutputFieldGroupCreate,
  OutputFieldGroupUpdate,
  OutputFieldGroupWithFields,
  OutputFieldGroupsPaginated,
  FormulaTestRequest,
  FormulaTestResponse,
  MessageResponse,
} from "@/types";

// ============== Output Field Groups ==============

interface ListOutputFieldGroupsParams {
  page?: number;
  page_size?: number;
}

export async function listOutputFieldGroups(
  versionId: number,
  params: ListOutputFieldGroupsParams = {}
): Promise<OutputFieldGroupsPaginated> {
  const response = await apiClient.get<OutputFieldGroupsPaginated>(
    `/simulators/versions/${versionId}/output-field-groups`,
    { params }
  );
  return response.data;
}

export async function listAllOutputFieldGroups(
  versionId: number
): Promise<OutputFieldGroupWithFields[]> {
  const response = await apiClient.get<OutputFieldGroupWithFields[]>(
    `/simulators/versions/${versionId}/output-field-groups/all`
  );
  return response.data;
}

export async function getOutputFieldGroup(
  versionId: number,
  groupId: number
): Promise<OutputFieldGroupWithFields> {
  const response = await apiClient.get<OutputFieldGroupWithFields>(
    `/simulators/versions/${versionId}/output-field-groups/${groupId}`
  );
  return response.data;
}

export async function createOutputFieldGroup(
  versionId: number,
  data: OutputFieldGroupCreate
): Promise<OutputFieldGroup> {
  const response = await apiClient.post<OutputFieldGroup>(
    `/simulators/versions/${versionId}/output-field-groups`,
    data
  );
  return response.data;
}

export async function updateOutputFieldGroup(
  versionId: number,
  groupId: number,
  data: OutputFieldGroupUpdate
): Promise<OutputFieldGroup> {
  const response = await apiClient.patch<OutputFieldGroup>(
    `/simulators/versions/${versionId}/output-field-groups/${groupId}`,
    data
  );
  return response.data;
}

export async function deleteOutputFieldGroup(
  versionId: number,
  groupId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/output-field-groups/${groupId}`
  );
  return response.data;
}

export async function reorderOutputFieldGroups(
  versionId: number,
  groupOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/output-field-groups/reorder`,
    { group_orders: groupOrders }
  );
  return response.data;
}

// ============== Output Fields ==============

interface ListOutputFieldsParams {
  page?: number;
  page_size?: number;
  group_id?: number;
  visible_only?: boolean;
}

export async function listOutputFields(
  versionId: number,
  params: ListOutputFieldsParams = {}
): Promise<OutputFieldsPaginated> {
  const response = await apiClient.get<OutputFieldsPaginated>(
    `/simulators/versions/${versionId}/output-fields`,
    { params }
  );
  return response.data;
}

export async function listAllOutputFields(
  versionId: number,
  visibleOnly = false
): Promise<OutputField[]> {
  const response = await apiClient.get<OutputField[]>(
    `/simulators/versions/${versionId}/output-fields/all`,
    { params: { visible_only: visibleOnly } }
  );
  return response.data;
}

export async function getOutputField(
  versionId: number,
  fieldId: number
): Promise<OutputField> {
  const response = await apiClient.get<OutputField>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}`
  );
  return response.data;
}

export async function createOutputField(
  versionId: number,
  data: OutputFieldCreate
): Promise<OutputField> {
  const response = await apiClient.post<OutputField>(
    `/simulators/versions/${versionId}/output-fields`,
    data
  );
  return response.data;
}

export async function updateOutputField(
  versionId: number,
  fieldId: number,
  data: OutputFieldUpdate
): Promise<OutputField> {
  const response = await apiClient.patch<OutputField>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}`,
    data
  );
  return response.data;
}

export async function deleteOutputField(
  versionId: number,
  fieldId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}`
  );
  return response.data;
}

export async function testOutputFieldFormula(
  versionId: number,
  fieldId: number,
  data: FormulaTestRequest
): Promise<FormulaTestResponse> {
  const response = await apiClient.post<FormulaTestResponse>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}/test`,
    data
  );
  return response.data;
}

export async function testFormula(
  versionId: number,
  data: FormulaTestRequest
): Promise<FormulaTestResponse> {
  const response = await apiClient.post<FormulaTestResponse>(
    `/simulators/versions/${versionId}/output-fields/test-formula`,
    data
  );
  return response.data;
}

export async function validateAllFormulas(
  versionId: number
): Promise<{
  valid: boolean;
  errors: { field: string; slug: string | null; error: string }[];
  warnings: { field: string; slug: string | null; warning: string }[];
}> {
  const response = await apiClient.post(
    `/simulators/versions/${versionId}/output-fields/validate-all`
  );
  return response.data;
}

export async function reorderOutputFields(
  versionId: number,
  fieldOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/output-fields/reorder`,
    { field_orders: fieldOrders }
  );
  return response.data;
}

export async function moveOutputField(
  versionId: number,
  fieldId: number,
  targetGroupId: number,
  newOrder: number
): Promise<OutputField> {
  const response = await apiClient.patch<OutputField>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}`,
    { group_id: targetGroupId, order: newOrder }
  );
  return response.data;
}

// ============== Slug Usage Check ==============

export interface OutputFieldSlugUsage {
  type: "formula";
  entity_type: string;
  entity_id: number;
  entity_name: string;
  description: string;
}

export interface OutputFieldSlugUsageResponse {
  slug: string;
  is_used: boolean;
  usages: OutputFieldSlugUsage[];
  can_edit_slug: boolean;
}

export async function checkOutputFieldSlugUsage(
  versionId: number,
  fieldId: number
): Promise<OutputFieldSlugUsageResponse> {
  const response = await apiClient.get<OutputFieldSlugUsageResponse>(
    `/simulators/versions/${versionId}/output-fields/${fieldId}/check-slug-usage`
  );
  return response.data;
}

